import React from 'react'

import { FormattedMessage, IntlProvider } from 'react-intl'
import { connect } from 'react-redux'

import type { Locale } from '@libs/typescript-utils'

import { localeActions } from '@services'

import { useAlertDispatch } from '@contexts/AlertProvider'

import { getLocale } from '@locales'

type LanguageLoadFailureListener = () => void
type SubscribeToLanguageLoadFailure = (listener: LanguageLoadFailureListener) => () => void

const LanguageLoadFailureContext = React.createContext<Nullable<SubscribeToLanguageLoadFailure>>(null)
LanguageLoadFailureContext.displayName = 'LanguageLoadFailureContext'

interface Translations {
  locale: Locale
  messages: Record<string, string>
}

interface IntlProviderWrapperProps {
  children: React.ReactNode
  locale: Locale
  restoreLocale: (locale: Locale) => void
}

function IntlProviderWrapper({ children, locale, restoreLocale }: IntlProviderWrapperProps) {
  const [translations, setTranslations] = React.useState<Nullable<Translations>>(null)
  const loadedLocale = React.useRef<Nullable<Locale>>(null)
  const failureListeners = React.useRef(new Set<LanguageLoadFailureListener>())

  const subscribeToLanguageLoadFailure = React.useCallback<SubscribeToLanguageLoadFailure>(listener => {
    failureListeners.current.add(listener)
    return () => {
      failureListeners.current.delete(listener)
    }
  }, [])

  React.useEffect(() => {
    let current = true
    getLocale(locale).then(
      messages => {
        if (current) {
          loadedLocale.current = locale
          setTranslations({ locale, messages })
        }
      },
      (error: unknown) => {
        // Nothing can render without translations, so the page-level fallback takes over the uncaught failure.
        if (loadedLocale.current === null) {
          throw error
        }
        if (current) {
          restoreLocale(loadedLocale.current)
          failureListeners.current.forEach(listener => listener())
        }
      }
    )
    return () => {
      current = false
    }
  }, [locale, restoreLocale])

  if (!translations) return null

  return (
    <LanguageLoadFailureContext.Provider value={subscribeToLanguageLoadFailure}>
      <IntlProvider locale={translations.locale} messages={translations.messages}>
        {children}
      </IntlProvider>
    </LanguageLoadFailureContext.Provider>
  )
}

const mapStateToProps = (state: Store) => ({
  locale: state.locale.language,
})

export const ConnectedIntlProvider = connect(mapStateToProps, {
  restoreLocale: localeActions.changeLocale.request,
})(IntlProviderWrapper)
ConnectedIntlProvider.displayName = 'ConnectedIntlProvider'

function useSubscribeToLanguageLoadFailure() {
  const subscribeToLanguageLoadFailure = React.useContext(LanguageLoadFailureContext)
  if (!subscribeToLanguageLoadFailure) {
    throw new Error('useSubscribeToLanguageLoadFailure must be used within ConnectedIntlProvider')
  }
  return subscribeToLanguageLoadFailure
}

/** Shows an error notice when a language fails to load; render it under both `ConnectedIntlProvider` and `AlertProvider`. */
export function LanguageLoadErrorAlert() {
  const subscribeToLanguageLoadFailure = useSubscribeToLanguageLoadFailure()
  const { setErrorAlert } = useAlertDispatch()

  React.useEffect(
    () =>
      subscribeToLanguageLoadFailure(() =>
        setErrorAlert(
          <FormattedMessage
            id="alerts.languageLoad.error"
            defaultMessage="A nyelv betöltése sikertelen, kérjük próbáld újra."
          />
        )
      ),
    [subscribeToLanguageLoadFailure, setErrorAlert]
  )

  return null
}
